import { Schema } from "effect";

/**
 * Opaque server-issued handle proving that the current FrontDesk+ flow was established by an eGK
 * card read.
 *
 * The value is a cryptographically random token minted by the Bridge card workflow. Clients treat it
 * as fully opaque: it carries no readable structure, is never parsed, and is never persisted. The
 * server stores only its SHA-256 digest and resolves all authority (practice scope, terminal,
 * workflow, patient binding, lifecycle phase) from its own record.
 *
 * Holding this handle is *not* authority to read or write patient master data. It only records that
 * a card read happened. It becomes patient-review authority solely by being committed through a real
 * check-in or card-create success and then activated — see {@link EgkAttestationHandle} usage in the
 * patient-info RPC group.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { EgkAttestationHandle } from "@ascleo/frontdesk-contracts/patient-info/egk-attestation";
 *
 * const handle = Schema.decodeUnknownSync(EgkAttestationHandle)("f3a9c1e0b7d2…");
 * ```
 */
export const EgkAttestationHandle = Schema.String.pipe(
  Schema.check(Schema.isMinLength(1)),
  Schema.check(Schema.isMaxLength(512)),
  Schema.brand("EgkAttestationHandle"),
);

/**
 * The decoded type of an {@link EgkAttestationHandle}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { EgkAttestationHandle } from "@ascleo/frontdesk-contracts/patient-info/egk-attestation";
 *
 * function carry(handle: EgkAttestationHandle | undefined): boolean {
 *   return handle !== undefined;
 * }
 * ```
 */
export type EgkAttestationHandle = typeof EgkAttestationHandle.Type;

/**
 * Optional attestation field mixed into the card-read, check-in, and create-patient contract
 * surfaces.
 *
 * The field is optional everywhere because the patient-info review is an optional subflow: when the
 * practice policy is disabled or MongoDB is unavailable, the server issues no attestation and the
 * ordinary eGK flow proceeds unchanged. A client that omits it simply never reaches the review.
 *
 * Supplying a handle is never sufficient authority on its own — the server re-derives every binding
 * from its own record, so a copied or forged value cannot turn a manual flow into an eligible one.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { OptionalEgkAttestation } from "@ascleo/frontdesk-contracts/patient-info/egk-attestation";
 *
 * const CheckInRequest = Schema.Struct({
 *   patientId: Schema.String,
 *   ...OptionalEgkAttestation.fields,
 * });
 * ```
 */
export const OptionalEgkAttestation = Schema.Struct({
  egkAttestation: Schema.optional(EgkAttestationHandle),
});

/**
 * The decoded type of the {@link OptionalEgkAttestation} field group.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { OptionalEgkAttestation } from "@ascleo/frontdesk-contracts/patient-info/egk-attestation";
 *
 * const carried: OptionalEgkAttestation = {};
 * ```
 */
export type OptionalEgkAttestation = typeof OptionalEgkAttestation.Type;
